import { useMemo } from "react";
import { bool, number, func, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { DescriptionModes } from "@repo/utils";
import { hasOlderOriginBuild, RISK_BUFFER_LOCK_BUILD } from "@repo/structure";
import {
  useEstimateStructure,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  ElementChatPopup,
  ItemBuffers,
  VisibilityButton, useEstimateEditorStore,
} from "@tools";
import { TooltipIcon } from "@components";
import {
  LibraryPusher,
  LibraryAccept,
  TaskRemove,
  TaskDescriptionTooltip,
  UserStoryGenerator,
  BreakdownRowActionMenu,
} from "./components";
import { EditDocument } from "@assets";
import { FileCopy } from "@material-ui/icons";
import classnames from "classnames";

export const BreakdownRowActions = observer(({
  element,
  tableDepth,
  handleDescriptionEdition,
  removable,
  toAccept,
  hideBadge,
  useMenu,
  expanded,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { showChatThread, allowEdition, isLibrary } = useEstimateEditorSettings();
  const { allDescriptionColumnsHidden } = useEstimateTableSettings();
  
  const { descriptionMode } = structure.settings;
  const { hasAnyDescriptions } = element;

  const commentPath = element.treePath.join("/");
  
  const showBuffers = useMemo(() => Boolean(
    !hasOlderOriginBuild(RISK_BUFFER_LOCK_BUILD, structure.sOriginBuild)
  ), [structure.sOriginBuild]);

  const isCreatedElement = useMemo(() => (
    !isLibrary || typeof element.id === "number"
  ), [element.id, isLibrary]);

  const lighterColor = useMemo(() => (
    tableDepth === 0 ? "color-primary-lighter" : undefined
  ), [tableDepth]);
  
  const showDescriptionTooltip = useMemo(() => (
    (!structure.visibility?.apply || !allDescriptionColumnsHidden) && descriptionMode === DescriptionModes.TOOLTIP
  ), [ structure.visibility?.apply, allDescriptionColumnsHidden, descriptionMode ]);
  
  const showDescriptionEditor = useMemo(() => (
    descriptionMode === DescriptionModes.TOOLTIP || (descriptionMode === DescriptionModes.ROW && !hasAnyDescriptions)
  ), [ hasAnyDescriptions, descriptionMode ]);
  
  const allowHidingDescription = useMemo(() => (
    hasAnyDescriptions && descriptionMode === DescriptionModes.TOOLTIP && visibilityMode
  ), [ hasAnyDescriptions, descriptionMode, visibilityMode ]);

  const handleDuplicate = () => {
    element.duplicateSelf(
      element.getDuplicateName(element.name || t("common.unnamed"))
    );
  };

  const handleDescriptionHide = (v) => {
    structure.sections.find(s => s.id === element.treePath[0])?.setHideAllDescriptionColumns(v);
  }

  return (
    <>
      {
        allowEdition && isCreatedElement && Boolean(element.name) && showBuffers &&
        <ItemBuffers
          element={element}
          lighterColor={lighterColor}
          elementExpanded={expanded}
        />
      }
      {
        !useMenu && allowEdition && isCreatedElement && Boolean(element.name) &&
        <UserStoryGenerator
          element={element}
          className={lighterColor}
        />
      }
      {
        allowEdition && isCreatedElement && showDescriptionEditor &&
        <TooltipIcon
          icon={<EditDocument />}
          onClick={handleDescriptionEdition}
          className={classnames("button-description", lighterColor, "noDrag hoverIcon opq")}
          color="primary"
        >
          {t(`views.editor.description${descriptionMode === DescriptionModes.ROW ? "_add" : ""}`)}
        </TooltipIcon>
      }
      {
        showDescriptionTooltip &&
        <TaskDescriptionTooltip
          element={ element }
          lighterColor={lighterColor}
        />
      }
      {
        showChatThread &&
        <ElementChatPopup
          rowThread
          scrollToThread={false}
          path={commentPath}
          taskName={element.name || t("common.unnamed")}
          className={lighterColor}
          hideBadge={hideBadge}
        />
      }
      {
        !useMenu && allowEdition && !isLibrary &&
        <TooltipIcon
          icon={<FileCopy />}
          color="primary"
          onClick={handleDuplicate}
          className={classnames("button-duplicate", lighterColor, "noDrag hoverIcon opq")}
        >
          {t("views.editor.duplicate", {
            type: t("proposal_parts.module_levels.level_" + tableDepth),
          })}
        </TooltipIcon>
      }
      {
        isLibrary && toAccept &&
        <LibraryAccept element={element} tableDepth={tableDepth} />
      }
      {
        !useMenu && allowEdition && !isLibrary && tableDepth > 0 &&
        <LibraryPusher element={element} tableDepth={tableDepth} />
      }
      {
        removable &&
        <TaskRemove
          element={element}
          tableDepth={tableDepth}
          isLibrary={isLibrary}
        />
      }
      {
        // allowDescriptionHide &&
        allowHidingDescription &&
        <VisibilityButton
          size="tiny" inRow
          hidden={allDescriptionColumnsHidden}
          onChange={handleDescriptionHide}
        />
      }
      {
        useMenu && allowEdition &&
        <BreakdownRowActionMenu
          element={element}
          className={lighterColor}
          tableDepth={tableDepth}
          showBuffers={showBuffers && isCreatedElement}
        />
      }
    </>
  );
});

BreakdownRowActions.propTypes = {
  element: object.isRequired,
  tableDepth: number,
  handleDescriptionEdition: func,
  removable: bool,
  toAccept: bool,
  hideBadge: bool,
  useMenu: bool,
  expanded: bool,
  allowDescriptionHide: bool,
};
